import { useAuthStore } from "@/stores/authStore";
import type { UserRole } from "@/types/auth";
import React from "react";

/**
 * Custom hook for role-based access control
 */
export const useRole = () => {
  const {
    user,
    hasRole,
    hasAnyRole,
    isAdmin,
    isHealthcareProvider,
    isTourOperator,
    isAffiliatePartner,
    isAffiliatePartnerAdmin,
    canAccessAdminPanel,
    canAccessAffiliatePartnerPanel,
  } = useAuthStore();

  // New role check functions
  const isTourOperatorAdmin = user?.role === "TOUR_OPERATOR_ADMIN";
  const isHealthcareProviderAdmin = user?.role === "HEALTHCARE_PROVIDER_ADMIN";
  const canManageTours = isAdmin() || isTourOperatorAdmin;
  const canManageHealthcareServices = isAdmin() || isHealthcareProviderAdmin;
  const canManageAffiliatePartnership = isAdmin() || isAffiliatePartnerAdmin();
  const canAccessTourOperatorPanel = isTourOperatorAdmin || isAdmin();
  const canAccessHealthcareProviderPanel =
    isHealthcareProviderAdmin || isAdmin();

  return {
    user,
    currentRole: user?.role,

    // Role checks
    hasRole,
    hasAnyRole,
    isAdmin: isAdmin(),
    isHealthcareProvider: isHealthcareProvider(),
    isHealthcareProviderAdmin,
    isTourOperator: isTourOperator(),
    isTourOperatorAdmin,
    isAffiliatePartner: isAffiliatePartner(),
    isAffiliatePartnerAdmin: isAffiliatePartnerAdmin(),
    isUser: user?.role === "USER",

    // Permission checks
    canAccessAdminPanel: canAccessAdminPanel(),
    canManageUsers: isAdmin(),
    canManageTours,
    canManageHealthcareServices,
    canManageAffiliatePartnership,
    canAccessTourOperatorPanel,
    canAccessHealthcareProviderPanel,
    canAccessAffiliatePartnerPanel: canAccessAffiliatePartnerPanel(),
    canViewAllRecords: isAdmin() || isHealthcareProvider(),
    canCreateHealthRecords: true, // All authenticated users
    canDeleteOwnRecords: true, // All authenticated users
    canModifyOthersRecords: isAdmin() || isHealthcareProvider(),

    // UI permission helpers.
    //
    // The admin menu follows the token, not the stored profile role. It used to
    // follow canAccessAdminPanel(), which is true for a profile role of ADMIN
    // or HEALTHCARE_PROVIDER — neither of which the admin pages accept, since
    // they gate on the Keycloak realm role. So the link appeared for people it
    // then refused, and did not appear for the staff who actually hold
    // staff_admin. Whatever gates the page has to be what shows the link.
    shouldShowAdminMenu: isAdmin(),
    shouldShowUserManagement: isAdmin(),
    shouldShowTourOperatorManagement: canAccessTourOperatorPanel,
    shouldShowHealthcareProviderManagement: canAccessHealthcareProviderPanel,
    shouldShowAffiliatePartnerManagement: canAccessAffiliatePartnerPanel(),
    shouldShowAdvancedFeatures:
      isAdmin() ||
      isHealthcareProvider() ||
      isTourOperator() ||
      isTourOperatorAdmin ||
      isHealthcareProviderAdmin ||
      isAffiliatePartner() ||
      isAffiliatePartnerAdmin(),
  };
};

/**
 * Component wrapper for role-based rendering
 */
interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireAdmin?: boolean;
  requireHealthcareProvider?: boolean;
  fallback?: React.ReactNode;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles,
  requireAdmin = false,
  requireHealthcareProvider = false,
  fallback = null,
}) => {
  const { hasAnyRole, isAdmin, isHealthcareProvider } = useRole();

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return <>{fallback}</>;
  }

  // Check healthcare provider requirement
  if (requireHealthcareProvider && !isHealthcareProvider) {
    return <>{fallback}</>;
  }

  // Check specific roles
  if (allowedRoles && !hasAnyRole(allowedRoles)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Higher-order component for role-based page protection
 */
export const withRoleProtection = <P extends object>(
  Component: React.ComponentType<P>,
  allowedRoles: UserRole[],
) => {
  return function ProtectedComponent(props: P) {
    const { hasAnyRole } = useRole();

    if (!hasAnyRole(allowedRoles)) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Access denied
            </h2>
            <p className="text-gray-600">
              You do not have the required permissions for this page.
            </p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};
